"use client";

import { useEffect } from "react";

/**
 * Scrolls to the URL's `#hash` section on history navigation.
 *
 * The App Router doesn't replay the browser's native "scroll to anchor" when
 * you navigate **back/forward** to a hashed URL (e.g. returning from `/start`
 * to `/#pricing`), so the page lands at the top. This listens for `popstate`
 * (back/forward) and `hashchange`, then scrolls the target section into view —
 * retrying across a few frames in case the destination route is still
 * rendering. Renders nothing; mount it once in the layout so the listener
 * survives client-side route changes.
 */
export function HashScroll() {
  useEffect(() => {
    const scrollToHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      let frames = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (frames++ < 12) {
          requestAnimationFrame(tick);
        }
      };
      tick();
    };

    window.addEventListener("popstate", scrollToHash);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      window.removeEventListener("popstate", scrollToHash);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  return null;
}
